import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { thinkingFrameworkService, ThinkingFrameworkSession } from '../../services/thinkingFrameworkService';
import PageHeader from '../common/PageHeader';
import FrameworkGuide from '../common/FrameworkGuide';
import './ThinkingFrameworks.css';

interface FirstPrinciplesAnalysisProps {
  onToggleSidebar: () => void;
}

interface FirstPrinciplesData {
  problem: string;
  assumptions: string[];
  breakdown: string; // The "Deconstruction" part
  basicTruths: string[];
  reconstruction: string; // The "Reconstruction" part (New Solution)
}

const FirstPrinciplesGuideContent = (
  <div>
    <h3>What is First Principles Thinking?</h3>
    <p>First principles thinking is a problem-solving approach that involves breaking a problem down into its basic building blocks (fundamental truths) and then rebuilding it from the ground up. It removes reliance on analogy ("doing what others do").</p>
    
    <h3>How to use it</h3>
    <ol>
      <li><strong>Identify the Problem:</strong> What are you trying to solve?</li>
      <li><strong>List Assumptions:</strong> What do you believe is true about this problem? (e.g., "Batteries are expensive")</li>
      <li><strong>Break Down (Deconstruction):</strong> Break the problem into its most basic elements. Ask "What is this made of?" or "What is physically true?"</li>
      <li><strong>Identify Basic Truths:</strong> Isolate the undeniable facts (First Principles) from the assumptions.</li>
      <li><strong>Reconstruct (Solution):</strong> Build a new solution from scratch using only these basic truths.</li>
    </ol>

    <h3>Example (Elon Musk on Batteries)</h3>
    <div className="framework-guide-example">
      <p><strong>Assumption:</strong> "Battery packs are really expensive and that's just the way they will always be."</p>
      <p><strong>First Principles:</strong> What are the material constituents of the batteries? Carbon, nickel, aluminum, and some polymers for separation, and a steel can.</p>
      <p><strong>Basic Truths:</strong> If we bought that on a metal exchange, what would each of those cost? $80/kWh.</p>
      <p><strong>Reconstruction:</strong> You just need to think of clever ways to take those materials and combine them into the shape of a battery cell, and you can have batteries that are much, much cheaper.</p>
    </div>
  </div>
);

const FirstPrinciplesAnalysis: React.FC<FirstPrinciplesAnalysisProps> = ({ onToggleSidebar }) => {
  const { sessionId } = useParams<{ sessionId?: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [session, setSession] = useState<ThinkingFrameworkSession | null>(null);
  const [title, setTitle] = useState('');
  const [data, setData] = useState<FirstPrinciplesData>({
    problem: '',
    assumptions: [],
    breakdown: '',
    basicTruths: [],
    reconstruction: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autoSaveTimer, setAutoSaveTimer] = useState<NodeJS.Timeout | null>(null);
  const [showGuide, setShowGuide] = useState(false);

  useEffect(() => {
    if (user) {
      if (sessionId) {
        loadSession();
      } else {
        // New session
        setTitle('');
        setData({
          problem: '',
          assumptions: [],
          breakdown: '',
          basicTruths: [],
          reconstruction: ''
        });
        setLoading(false);
        setShowGuide(true);
      }
    }
  }, [user, sessionId]);

  const loadSession = async () => {
    if (!sessionId || !user) return;

    try {
      setLoading(true);
      setError(null);
      const loadedSession = await thinkingFrameworkService.getSession(sessionId);
      setSession(loadedSession);
      setTitle(loadedSession.title);
      
      const loadedData = loadedSession.framework_data as FirstPrinciplesData;
      setData({
        problem: loadedData.problem || '',
        assumptions: loadedData.assumptions || [],
        breakdown: loadedData.breakdown || '',
        basicTruths: loadedData.basicTruths || [],
        reconstruction: loadedData.reconstruction || ''
      });
    } catch (err) {
      console.error('Error loading session:', err);
      setError('Failed to load session');
    } finally {
      setLoading(false);
    }
  };

  const saveSession = useCallback(async (dataToSave: { title?: string; framework_data?: FirstPrinciplesData }) => {
    if (!user) return;

    try {
      setSaving(true);
      setError(null);

      if (sessionId && session) {
        // Update existing session
        const updated = await thinkingFrameworkService.updateSession(sessionId, dataToSave);
        setSession(updated);
      } else {
        // Create new session
        const newSession = await thinkingFrameworkService.createSession({
          framework_type: 'first_principles',
          title: dataToSave.title || 'Untitled First Principles Analysis',
          framework_data: dataToSave.framework_data || data
        });
        setSession(newSession);
        navigate(`/thinking-frameworks/first_principles/${newSession.session_id}`, { replace: true });
      }
    } catch (err) {
      console.error('Error saving session:', err);
      setError('Failed to save session');
    } finally {
      setSaving(false);
    }
  }, [user, sessionId, session, data, navigate]);

  // Debounced auto-save
  useEffect(() => {
    if (autoSaveTimer) {
      clearTimeout(autoSaveTimer);
    }

    const hasContent = title || data.problem || data.breakdown || data.reconstruction;

    if (hasContent) {
      const timer = setTimeout(() => {
        saveSession({ title, framework_data: data });
      }, 2000); // Auto-save after 2 seconds
      setAutoSaveTimer(timer);
    }

    return () => {
      if (autoSaveTimer) {
        clearTimeout(autoSaveTimer);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, data]);

  const handleTitleChange = (newTitle: string) => {
    setTitle(newTitle);
  };

  const handleDataChange = (field: keyof FirstPrinciplesData, value: string | string[]) => {
    setData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleListChange = (field: 'assumptions' | 'basicTruths', value: string) => {
    const list = value.split('\n');
    handleDataChange(field, list);
  };

  const handleDelete = async () => {
    if (!sessionId) return;
    if (!window.confirm('Are you sure you want to delete this analysis?')) return;

    try {
      setError(null);
      await thinkingFrameworkService.deleteSession(sessionId);
      navigate('/thinking-frameworks');
    } catch (err) {
      console.error('Error deleting session:', err);
      setError('Failed to delete session');
    }
  };

  const handleBack = () => {
    navigate('/thinking-frameworks');
  };

  if (loading) {
    return (
      <div className="thinking-framework-page">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<h1 className="thinking-framework-header-title">/first-principles</h1>}
        />
        <div className="thinking-framework-content">
          <div className="thinking-framework-loading">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="thinking-framework-page">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={<h1 className="thinking-framework-header-title">/first-principles</h1>}
        rightActions={
          <div className="thinking-framework-actions">
            {saving && <span className="thinking-framework-saving">Saving...</span>}
            <button className="framework-guide-btn" onClick={() => setShowGuide(true)}>
              💡 Guide
            </button>
            {sessionId && (
              <button className="thinking-framework-delete-btn" onClick={handleDelete}>
                Delete
              </button>
            )}
            <button className="thinking-framework-back-btn" onClick={handleBack}>
              ← Back
            </button>
          </div>
        }
      />
      
      <div className="thinking-framework-content">
        {error && (
          <div className="thinking-framework-error">{error}</div>
        )}

        <div className="thinking-framework-title-section">
          <input
            type="text"
            value={title}
            onChange={(e) => handleTitleChange(e.target.value)}
            placeholder="Analysis Title..."
            className="thinking-framework-title-input"
          />
        </div>

        <div className="first-principles-container">
          <div className="framework-section">
            <h3>1. Identify the Problem</h3>
            <textarea
              value={data.problem}
              onChange={(e) => handleDataChange('problem', e.target.value)}
              placeholder="What specific problem are you trying to solve?"
              rows={3}
            />
          </div>

          <div className="framework-grid-2">
            <div className="framework-section">
              <h3>2. List Current Assumptions</h3>
              <p className="framework-hint">What do you think is true about this? (One per line)</p>
              <textarea
                value={data.assumptions.join('\n')}
                onChange={(e) => handleListChange('assumptions', e.target.value)}
                placeholder="e.g. It's too expensive..."
                rows={6}
              />
            </div>

            <div className="framework-section">
              <h3>3. Deconstruct</h3>
              <p className="framework-hint">Break it down to the fundamental reality.</p>
              <textarea
                value={data.breakdown}
                onChange={(e) => handleDataChange('breakdown', e.target.value)}
                placeholder="What are the basic parts? What is undeniably true?"
                rows={6}
              />
            </div>
          </div>

          <div className="framework-section">
            <h3>4. Basic Truths (First Principles)</h3>
            <p className="framework-hint">The fundamental facts that remain after deconstruction. (One per line)</p>
            <textarea
              value={data.basicTruths.join('\n')}
              onChange={(e) => handleListChange('basicTruths', e.target.value)}
              placeholder="e.g. Material cost is $80..."
              rows={4}
            />
          </div>

          <div className="framework-section reconstruction-section">
            <h3>5. Reconstruction (New Solution)</h3>
            <p className="framework-hint">Build a new solution from the ground up using your basic truths.</p>
            <textarea
              value={data.reconstruction}
              onChange={(e) => handleDataChange('reconstruction', e.target.value)}
              placeholder="How can you combine these truths to solve the problem in a new way?"
              rows={6}
            />
          </div>
        </div>
      </div>

      <FrameworkGuide
        isOpen={showGuide}
        onClose={() => setShowGuide(false)}
        title="First Principles Thinking"
        content={FirstPrinciplesGuideContent}
      />
    </div>
  );
};

export default FirstPrinciplesAnalysis;

